import React, { useState, useEffect, useMemo } from "react";
import { Table, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { TableProps, ColumnsType } from "antd/es/table";
import "antd/dist/reset.css";
import "../../../styles/charts/LeadSourcesDataTable.scss"; // Import the SCSS file
import CommonSelect from "../../../../../../core/common/commonSelect";
import {
  formatNumber,
  formatPercentage,
} from "../../../helpers/numbers.helper";

interface SourceData {
  source: string;
  total_leads: number;
  converted: number;
  lost: number;
  in_process: number;
  conversion_rate: number;
  children?: SourceData[];
  key?: string;
}

interface LeadSourcesDataTableProps {
  data: any;
  loading?: boolean;
  error?: string;
  showPagination?: boolean;
  pageSize?: number;
  showSearch?: boolean;
  maxHeight?: number;
}

const LeadSourcesDataTable: React.FC<LeadSourcesDataTableProps> = ({
  data,
  loading = false,
  error,
  showPagination = true,
  pageSize = 4,
  showSearch = true,
  maxHeight = 280,
}) => {
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState<SourceData[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<string>("-1");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const currentData = useMemo(() => {
    if (selectedPipeline === "-1") {
      return data?.total || [];
    }
    return data?.by_pipeline?.[selectedPipeline] || [];
  }, [data, selectedPipeline]);

  const addKeysToData = (
    dataArray: SourceData[],
    parentKey: string = ""
  ): SourceData[] => {
    return dataArray.map((item, index) => {
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      const processedItem = {
        ...item,
        key,
      };

      if (item.children && item.children.length > 0) {
        processedItem.children = addKeysToData(item.children, key);
      }

      return processedItem;
    });
  };

  const processedData = useMemo(() => {
    return addKeysToData(currentData);
  }, [currentData]);

  const searchInData = (
    dataArray: SourceData[],
    searchValue: string
  ): SourceData[] => {
    const searchLower = searchValue.toLowerCase();
    return dataArray
      .map((item) => {
        const matches = Object.entries(item).some(([key, value]) => {
          if (key === "children" || key === "key") return false;
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchLower);
        });

        let filteredChildren: SourceData[] | undefined;
        if (item.children && item.children.length > 0) {
          filteredChildren = searchInData(item.children, searchValue);
        }

        if (matches || (filteredChildren && filteredChildren.length > 0)) {
          return {
            ...item,
            children:
              filteredChildren && filteredChildren.length > 0
                ? filteredChildren
                : item.children,
          };
        }

        return null;
      })
      .filter((item): item is any => item !== null);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value) {
      setFilteredData(processedData);
      setExpandedRowKeys([]);
      return;
    }

    const filtered = searchInData(processedData, value);
    setFilteredData(filtered);

    const getAllKeys = (dataArray: SourceData[]): string[] => {
      const keys: string[] = [];
      dataArray.forEach((item) => {
        if (item.children && item.children.length > 0) {
          keys.push(item.key!);
          keys.push(...getAllKeys(item.children));
        }
      });
      return keys;
    };
    setExpandedRowKeys(getAllKeys(filtered));
  };

  useEffect(() => {
    setFilteredData(processedData);
    setSearchText("");
    setExpandedRowKeys([]);
  }, [processedData]);

  if (error) {
    return (
      <div className="table-error-state">
        <i className="ti ti-alert-circle text-danger" />
        <p className="text-danger">Error loading data</p>
        <small className="text-muted">{error}</small>
      </div>
    );
  }

  const columns: ColumnsType<SourceData> = [
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (text: string) => (
        <div className="d-flex align-items-center">
          <h6 className="fs-13 fw-medium mb-0">{text}</h6>
        </div>
      ),
      sorter: (a, b) => (a.source || "").localeCompare(b.source || ""),
      width: 200,
    },
    {
      title: "Total",
      dataIndex: "total_leads",
      key: "total_leads",
      render: (text: number) => (
        <h6 className="badge badge-secondary-transparent">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a, b) => a.total_leads - b.total_leads,
      align: "center",
      width: 120,
    },
    {
      title: "Converted",
      dataIndex: "converted",
      key: "converted",
      render: (text: number) => (
        <h6 className="badge badge-success-transparent">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a, b) => a.converted - b.converted,
      align: "center",
      width: 120,
    },
    {
      title: "Lost",
      dataIndex: "lost",
      key: "lost",
      render: (text: number) => (
        <h6 className="badge badge-danger-transparent">{formatNumber(text)}</h6>
      ),
      sorter: (a, b) => a.lost - b.lost,
      align: "center",
      width: 120,
    },
    {
      title: "In Process",
      dataIndex: "in_process",
      key: "in_process",
      render: (text: number) => (
        <h6 className="badge badge-purple-transparent">{formatNumber(text)}</h6>
      ),
      sorter: (a, b) => a.in_process - b.in_process,
      align: "center",
      width: 120,
    },
    {
      title: "Conversion Rate",
      dataIndex: "conversion_rate",
      key: "conversion_rate",
      render: (text: number) => (
        <h6 className="badge badge-info-transparent">
          {formatPercentage(text)}
        </h6>
      ),
      sorter: (a, b) => a.conversion_rate - b.conversion_rate,
      align: "center",
      width: 150,
    },
  ];

  const pipelineOptions =
    data?.pipelines?.map((pipeline: any) => ({
      value: pipeline?.id?.toString(),
      label: pipeline?.pipeline_name,
    })) || [];

  const tableProps: TableProps<SourceData> = {
    columns,
    dataSource: filteredData,
    loading,
    rowKey: "key",
    scroll: {
      y: maxHeight,
      x: "max-content",
    },
    pagination: showPagination
      ? {
        pageSize,
        showSizeChanger: false,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
      }
      : false,
    expandable: {
      expandedRowKeys,
      onExpandedRowsChange: (keys: any) => setExpandedRowKeys(keys),
      indentSize: 30,
    },
  };

  return (
    <div className="lead-sources-data-table-container">
      <div className="table-controls">
        <div className="pipeline-filter" style={{ width: "160px" }}>
          <CommonSelect
            className="select small text-muteds"
            options={[
              { value: "-1", label: "All Pipelines" },
              ...pipelineOptions,
            ]}
            defaultValue={
              selectedPipeline
                ? pipelineOptions.find(
                  (pipeline: any) => pipeline.value === selectedPipeline
                )
                : { value: "-1", label: "All Pipelines" }
            }
            onChange={(selected: any) => {
              setSelectedPipeline(selected.value);
              setExpandedRowKeys([]);
            }}
            placeHolder="Filter By Pipeline"
          />
        </div>

        {showSearch && (
          <div className="search-wrapper">
            <Input
              style={{ width: "140px" }}
              className="p-2"
              placeholder="Search across all columns..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </div>
        )}

        <button
          className="btn btn-outline-secondary btn-md expand-collapse-btn"
          onClick={() => {
            if (expandedRowKeys.length > 0) {
              setExpandedRowKeys([]);
            } else {
              const getAllKeys = (dataArray: SourceData[]): string[] => {
                const keys: string[] = [];
                dataArray.forEach((item) => {
                  if (item.children && item.children.length > 0) {
                    keys.push(item.key!);
                    keys.push(...getAllKeys(item.children));
                  }
                });
                return keys;
              };
              setExpandedRowKeys(getAllKeys(filteredData));
            }
          }}>
          {expandedRowKeys.length > 0 ? "Collapse" : "Expand"}
        </button>
      </div>

      <Table {...tableProps} />
    </div>
  );
};

export default LeadSourcesDataTable;
